import { camelizeKeys, mapKeysToCamelCase } from '@skytech/utils'

import {
  API_PATH_ECOM_POLICY_LIST,
  API_PATH_ECOM_POLICY,
  API_PATH_ECOM_POLICY_ORDER_DETAILS,
  API_PATH_ECOM_POLICY_GREENPOOL_ORDER_DETAILS,
  API_PATH_ECOM_POLICY_MANDATORY_PRODUCTS,
} from '@/api/apiRoutes'
import type {
  Policy,
  OrderCoreData,
  PolicyCoreData,
  Order,
  MainGreenpoolOrder,
  GreenpoolOrderProductOriginal,
  GreenpoolOrderOriginal,
  EcomPolicyMandatoryProduct,
  GetMandatoryProductsResponse,
} from '@/components/ecom-policy/types'
import { get } from '@/util/auth'

export const ecomPolicyResources = {
  getList: async () => {
    const policyList = await get(API_PATH_ECOM_POLICY_LIST)
    const parsedData = camelizeKeys(policyList) as {
      policyList: PolicyCoreData[]
      orders: OrderCoreData[]
      greenpoolOrders: MainGreenpoolOrder[]
    }

    // Sort policyList: unlocked alphabetically, then locked by nextOrderDate
    const sortedPolicyList = parsedData.policyList.sort((a, b) => {
      // If locked status differs, unlocked items come first
      if (a.isLocked !== b.isLocked) {
        return a.isLocked - b.isLocked
      }

      // If both are locked, sort by nextOrderDate
      if (a.isLocked && b.isLocked) {
        return new Date(a.nextOrderDate).getTime() - new Date(b.nextOrderDate).getTime()
      }

      // If both are unlocked, sort alphabetically by name
      return a.name.localeCompare(b.name)
    })

    return { ...parsedData, policyList: sortedPolicyList }
  },

  getMandatoryProducts: async (
    policyId: number,
    selectedProductIds: number[] = []
  ): Promise<GetMandatoryProductsResponse> => {
    const queryString = selectedProductIds.map(id => `productIds=${id}`).join('&')

    const url =
      API_PATH_ECOM_POLICY_MANDATORY_PRODUCTS.replace(':policyId', String(policyId)) +
      '?' +
      queryString
    const response = await get<EcomPolicyMandatoryProduct[]>(url)
    const parsedData = camelizeKeys(response)

    return {
      policyId,
      mandatoryProducts: parsedData as EcomPolicyMandatoryProduct[],
    }
  },

  get: async (policyId: number) => {
    const policy = await get(API_PATH_ECOM_POLICY.replace(':policyId', String(policyId)))
    const parsedData = camelizeKeys(policy) as Policy
    const transportationProducts =
      parsedData.vendorId === 7
        ? parsedData.products.filter(product => product.category === 'SHIPPING')
        : []

    const { products } = parsedData

    parsedData.products = products.map(product => {
      const parsed = JSON.parse(product.parentProductIdList || '[]')
      const filtered = parsed.filter((id: any) => id !== null)

      return {
        ...product,
        parentProductIdList: filtered.join(','),
      }
    })

    return { ...parsedData, transportationProducts }
  },

  getOrder: async (orderId: number) => {
    const order = await get(API_PATH_ECOM_POLICY_ORDER_DETAILS.replace(':orderId', String(orderId)))
    const parsedData = camelizeKeys(order)

    return parsedData as Order
  },

  getGreenpoolOrder: async (orderId: number) => {
    const order = await get<{
      order: GreenpoolOrderOriginal[]
      products: GreenpoolOrderProductOriginal[]
    }>(API_PATH_ECOM_POLICY_GREENPOOL_ORDER_DETAILS.replace(':orderId', String(orderId)))
    const parsedData = mapKeysToCamelCase(order)

    return {
      ...parsedData.order[0],
      products: parsedData.products,
    }
  },
}
